import { Pipe, PipeTransform } from '@angular/core';

/**
 * Decodes HTML entities (including multiply-encoded ones) so that content is
 * passed to the markdown renderer as plain text.
 *
 * Why this exists:
 * Model/back-end responses sometimes arrive HTML-encoded (e.g. `'` as `&#x27;`,
 * `&` as `&amp;`), occasionally encoded more than once (`&amp;#x27;`,
 * `&amp;amp;#39;`). When that encoded text reaches `marked`, it escapes the
 * leading `&` again inside code blocks (`&#x27;` -> `&amp;#x27;`), so the browser
 * renders the literal entity text instead of the intended character.
 *
 * Decoding here — at the render boundary, right before `[data]` hits `marked` —
 * guarantees no upstream code path can bypass it. The transform is idempotent for
 * already-clean text, so it is safe even when content was decoded earlier.
 */
@Pipe({
  name: 'decodeHtmlEntities',
  standalone: true,
})
export class DecodeHtmlEntitiesPipe implements PipeTransform {
  private static readonly NAMED_ENTITIES: { [key: string]: string } = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    '#39': "'",
    '#x27': "'",
    '#x2F': '/',
    '#47': '/',
    '#60': '<',
    '#62': '>',
  };

  // Cap the number of decode passes so a pathological input can't loop forever.
  private static readonly MAX_PASSES = 8;

  transform(text: string | null | undefined): string {
    if (!text) return '';
    if (!text.includes('&')) return text;

    let decoded = text;
    let previous: string;
    let passes = 0;

    do {
      previous = decoded;
      decoded = this.decodeOnePass(decoded);
      passes++;
    } while (decoded !== previous && passes < DecodeHtmlEntitiesPipe.MAX_PASSES);

    return decoded;
  }

  private decodeOnePass(str: string): string {
    return str.replace(/&([^;\s]+);/g, (match, entity) => {
      const named = DecodeHtmlEntitiesPipe.NAMED_ENTITIES[entity];
      if (named) return named;

      if (entity.startsWith('#')) {
        const code = entity.startsWith('#x')
          ? parseInt(entity.substring(2), 16)
          : parseInt(entity.substring(1), 10);
        if (!isNaN(code)) {
          return String.fromCharCode(code);
        }
      }

      return match;
    });
  }
}
